import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import {
  DatingProfile,
  SMOKING_OPTIONS,
  DRINKING_OPTIONS,
  EXERCISE_OPTIONS,
  WANTS_CHILDREN_OPTIONS,
  RELATIONSHIP_OPTIONS,
  AvailabilityWindow,
  parseAvailabilityWindows,
  serializeAvailabilityWindows,
} from '../../../types/profile';
import { AvailabilityPicker } from '../../profile/AvailabilityPicker';

interface Stage3Props extends StageProps {
  step: number;
}

const DIET_OPTIONS = [
  { value: 'none', label: 'No restrictions' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'other', label: 'Other' },
];

const PET_OPTIONS = [
  { value: 'none', label: 'No pets' },
  { value: 'dog', label: 'Dog(s)' },
  { value: 'cat', label: 'Cat(s)' },
  { value: 'both', label: 'Dogs and cats' },
  { value: 'other', label: 'Other pets' },
];

const HAS_CHILDREN_OPTIONS = [
  { value: true, label: 'Yes, I have children' },
  { value: false, label: 'No children' },
];

const Stage3Lifestyle: React.FC<Stage3Props> = ({
  onNext,
  onBack,
  isLoading,
  step,
}) => {
  const [profile, setProfile] = useState<Partial<DatingProfile>>({});

  useEffect(() => {
    const loadProfile = async () => {
      const existing = await profileService.getProfile();
      if (existing) {
        setProfile(existing);
      }
    };
    loadProfile();
  }, []);

  const updateField = (field: keyof DatingProfile, value: unknown) => {
    setProfile((prev: Partial<DatingProfile>) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    await profileService.updateProfile(profile);
    onNext(profile as Record<string, unknown>);
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Habits & Lifestyle</h3>

      <div className="wizard-form-group">
        <label>Smoking</label>
        <div className="wizard-options-grid">
          {SMOKING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.smoking === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('smoking', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Drinking</label>
        <div className="wizard-options-grid">
          {DRINKING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.drinking === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('drinking', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Diet</label>
        <div className="wizard-options-grid">
          {DIET_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.diet === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('diet', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Activity & Pets</h3>

      <div className="wizard-form-group">
        <label>Exercise Frequency</label>
        <div className="wizard-options-grid">
          {EXERCISE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.exercise === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('exercise', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Pets</label>
        <div className="wizard-options-grid">
          {PET_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.petTypes === option.value ? 'selected' : ''
              }`}
              onClick={() => {
                updateField('hasPets', option.value !== 'none');
                updateField('petTypes', option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Family & Relationship Goals</h3>

      <div className="wizard-form-group">
        <label>What are you looking for?</label>
        <div className="wizard-options-grid">
          {RELATIONSHIP_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.lookingFor === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('lookingFor', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Do you have children?</label>
        <div className="wizard-options-grid">
          {HAS_CHILDREN_OPTIONS.map((option) => (
            <button
              key={String(option.value)}
              type="button"
              className={`wizard-option-btn ${
                profile.hasChildren === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('hasChildren', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>Do you want children?</label>
        <div className="wizard-options-grid">
          {WANTS_CHILDREN_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.wantsChildren === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('wantsChildren', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-form-group">
        <label>When are you usually around?</label>
        <AvailabilityPicker
          value={parseAvailabilityWindows(profile.availabilityWindowsJson)}
          onChange={(windows: AvailabilityWindow[]) =>
            updateField('availabilityWindowsJson', serializeAvailabilityWindows(windows))
          }
        />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="wizard-stage">
      {renderCurrentStep()}
      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage3Lifestyle;
